import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { UserPlus, UserMinus, Loader2 } from 'lucide-react';

interface Profile {
  id: string;
  name: string;
  profile_image?: string;
  preferred_language: string;
}

interface GroupMembersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  chatId: string;
  isGroup: boolean;
  members: Profile[];
  onMembersUpdate: () => void;
}

export const GroupMembersDialog = ({
  open,
  onOpenChange,
  chatId,
  isGroup,
  members,
  onMembersUpdate,
}: GroupMembersDialogProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [mode, setMode] = useState<'view' | 'add'>('view');
  const [availableContacts, setAvailableContacts] = useState<Profile[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && mode === 'add') {
      fetchAvailableContacts();
    }
  }, [open, mode]);

  const fetchAvailableContacts = async () => {
    if (!user) return;

    try {
      // Get all chat participants for chats the user is in
      const { data: userChats, error: chatsError } = await supabase
        .from('chat_participants')
        .select('chat_id')
        .eq('user_id', user.id);

      if (chatsError) throw chatsError;

      if (!userChats || userChats.length === 0) {
        setAvailableContacts([]);
        return;
      }

      const chatIds = userChats.map(c => c.chat_id);

      // Get all other participants in those chats
      const { data: otherParticipants, error: participantsError } = await supabase
        .from('chat_participants')
        .select('user_id')
        .in('chat_id', chatIds)
        .neq('user_id', user.id);

      if (participantsError) throw participantsError;

      if (!otherParticipants || otherParticipants.length === 0) {
        setAvailableContacts([]);
        return;
      }

      // Get unique user IDs that are NOT already members of this group
      const memberIds = members.map(m => m.id);
      const uniqueUserIds = [...new Set(otherParticipants.map(p => p.user_id))]
        .filter(id => !memberIds.includes(id));

      if (uniqueUserIds.length === 0) {
        setAvailableContacts([]);
        return;
      }

      // Fetch profiles using the secure RPC function
      const profilePromises = uniqueUserIds.map(userId =>
        supabase.rpc('get_chat_participant_profile', { _profile_id: userId })
      );

      const profileResults = await Promise.all(profilePromises);
      const validProfiles: Profile[] = [];

      for (const result of profileResults) {
        if (!result.error && result.data && result.data.length > 0) {
          validProfiles.push({
            id: result.data[0].id,
            name: result.data[0].name,
            profile_image: result.data[0].profile_image,
            preferred_language: result.data[0].preferred_language,
          });
        }
      }

      setAvailableContacts(validProfiles);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  const removeMember = async (memberId: string) => {
    if (memberId === user?.id) {
      toast({
        title: 'Error',
        description: 'You cannot remove yourself',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from('chat_participants')
        .delete()
        .eq('chat_id', chatId)
        .eq('user_id', memberId);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Member removed successfully',
      });
      onMembersUpdate();
    } catch (error: any) {
      console.error('Error removing member:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove member',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const addMembers = async () => {
    if (selectedUsers.length === 0) return;

    setLoading(true);
    try {
      const participants = selectedUsers.map(userId => ({
        chat_id: chatId,
        user_id: userId,
      }));

      const { error } = await supabase
        .from('chat_participants')
        .insert(participants);

      if (error) throw error;

      toast({
        title: 'Success',
        description: `${selectedUsers.length} member${selectedUsers.length > 1 ? 's' : ''} added`,
      });
      setSelectedUsers([]);
      setMode('view');
      onMembersUpdate();
    } catch (error: any) {
      console.error('Error adding members:', error);
      toast({
        title: 'Error',
        description: 'Failed to add members',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleUser = (userId: string) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const filteredProfiles = availableContacts.filter(profile =>
    profile.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isGroup) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {mode === 'view' ? 'Group Members' : 'Add Members'}
          </DialogTitle>
        </DialogHeader>

        {mode === 'view' ? (
          <>
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-muted-foreground">
                {members.length} member{members.length !== 1 ? 's' : ''}
              </span>
              <Button
                size="sm"
                onClick={() => setMode('add')}
                disabled={loading}
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Add Members
              </Button>
            </div>

            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-2">
                {members.map((member) => (
                  <div
                    key={member.id}
                    className="flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-muted/50"
                  >
                    <Avatar>
                      <AvatarImage src={member.profile_image} />
                      <AvatarFallback>{member.name[0]}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold truncate">{member.name}</h3>
                    </div>
                    {member.id !== user?.id && (
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => removeMember(member.id)}
                        disabled={loading}
                      >
                        <UserMinus className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </ScrollArea>
          </>
        ) : (
          <>
            <Input
              placeholder="Search contacts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="mb-2"
            />

            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-2">
                {filteredProfiles.map((profile) => (
                  <label
                    key={profile.id}
                    className="flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-muted/50 cursor-pointer"
                  >
                    <Checkbox
                      checked={selectedUsers.includes(profile.id)}
                      onCheckedChange={() => toggleUser(profile.id)}
                    />
                    <Avatar>
                      <AvatarImage src={profile.profile_image} />
                      <AvatarFallback>{profile.name[0]}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold truncate">{profile.name}</h3>
                    </div>
                  </label>
                ))}

                {filteredProfiles.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    No contacts found
                  </div>
                )}
              </div>
            </ScrollArea>

            <div className="flex gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setMode('view');
                  setSelectedUsers([]);
                  setSearchQuery('');
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={addMembers}
                disabled={loading || selectedUsers.length === 0}
                className="flex-1"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  `Add ${selectedUsers.length || ''}`
                )}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
